import PipelinesAPI from 'dashboard/api/pipelines/pipelines';

const toNumber = value => {
  const parsed = Number(value);
  return Number.isNaN(parsed) ? value : parsed;
};

const syncPipelineStages = (_state, pipelineId, stages) => {
  const normalizedPipelineId = toNumber(pipelineId);
  _state.stages[normalizedPipelineId] = stages;

  const pipeline = _state.records[normalizedPipelineId];
  if (pipeline) {
    _state.records[normalizedPipelineId] = {
      ...pipeline,
      stages,
    };
  }
};

const state = {
  records: {},
  stages: {},
  uiFlags: {
    isFetching: false,
    isFetchingStages: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
  },
};

export const getters = {
  getPipelines(_state) {
    return Object.values(_state.records).sort((a, b) => a.position - b.position);
  },
  getPipeline: _state => id => {
    return _state.records[id] || {};
  },
  getStagesByPipeline: _state => pipelineId => {
    const stages = _state.stages[pipelineId] || [];
    return stages.sort((a, b) => a.position - b.position);
  },
  getUIFlags(_state) {
    return _state.uiFlags;
  },
};

export const actions = {
  get: async ({ commit }, { accountId }) => {
    commit('SET_PIPELINES_UI_FLAG', { isFetching: true });
    try {
      const response = await PipelinesAPI.get(accountId);
      commit('SET_PIPELINES', response.data);
    } catch (error) {
      throw error;
    } finally {
      commit('SET_PIPELINES_UI_FLAG', { isFetching: false });
    }
  },

  show: async ({ commit }, { accountId, pipelineId }) => {
    commit('SET_PIPELINES_UI_FLAG', { isFetching: true });
    try {
      const response = await PipelinesAPI.show(accountId, pipelineId);
      commit('ADD_PIPELINE', response.data);
      return response.data;
    } catch (error) {
      throw error;
    } finally {
      commit('SET_PIPELINES_UI_FLAG', { isFetching: false });
    }
  },

  create: async ({ commit }, { accountId, pipeline }) => {
    commit('SET_PIPELINES_UI_FLAG', { isCreating: true });
    try {
      const response = await PipelinesAPI.create(accountId, { pipeline });
      commit('ADD_PIPELINE', response.data);
      return response.data;
    } catch (error) {
      throw error;
    } finally {
      commit('SET_PIPELINES_UI_FLAG', { isCreating: false });
    }
  },

  update: async ({ commit }, { accountId, pipelineId, pipeline }) => {
    commit('SET_PIPELINES_UI_FLAG', { isUpdating: true });
    try {
      const response = await PipelinesAPI.update(accountId, pipelineId, { pipeline });
      commit('EDIT_PIPELINE', response.data);
      return response.data;
    } catch (error) {
      throw error;
    } finally {
      commit('SET_PIPELINES_UI_FLAG', { isUpdating: false });
    }
  },

  createFromTemplate: async ({ commit }, { accountId, templateId }) => {
    commit('SET_PIPELINES_UI_FLAG', { isCreating: true });
    try {
      const response = await PipelinesAPI.createFromTemplate(accountId, templateId);
      commit('ADD_PIPELINE', response.data);
      return response.data;
    } catch (error) {
      throw error;
    } finally {
      commit('SET_PIPELINES_UI_FLAG', { isCreating: false });
    }
  },

  delete: async ({ commit }, { accountId, pipelineId }) => {
    commit('SET_PIPELINES_UI_FLAG', { isDeleting: true });
    try {
      await PipelinesAPI.delete(accountId, pipelineId);
      commit('DELETE_PIPELINE', pipelineId);
    } catch (error) {
      throw error;
    } finally {
      commit('SET_PIPELINES_UI_FLAG', { isDeleting: false });
    }
  },

  getStages: async ({ commit }, { accountId, pipelineId }) => {
    commit('SET_PIPELINES_UI_FLAG', { isFetchingStages: true });
    try {
      const response = await PipelinesAPI.getStages(accountId, pipelineId);
      commit('SET_PIPELINE_STAGES', { pipelineId, stages: response.data });
    } catch (error) {
      throw error;
    } finally {
      commit('SET_PIPELINES_UI_FLAG', { isFetchingStages: false });
    }
  },

  createStage: async ({ commit }, { accountId, pipelineId, stage }) => {
    commit('SET_PIPELINES_UI_FLAG', { isCreating: true });
    try {
      const response = await PipelinesAPI.createStage(accountId, pipelineId, { pipeline_stage: stage });
      commit('ADD_PIPELINE_STAGE', { pipelineId, stage: response.data });
      return response.data;
    } catch (error) {
      throw error;
    } finally {
      commit('SET_PIPELINES_UI_FLAG', { isCreating: false });
    }
  },

  updateStage: async ({ commit }, { accountId, pipelineId, stageId, stage }) => {
    commit('SET_PIPELINES_UI_FLAG', { isUpdating: true });
    try {
      const response = await PipelinesAPI.updateStage(accountId, pipelineId, stageId, { pipeline_stage: stage });
      commit('EDIT_PIPELINE_STAGE', { pipelineId, stage: response.data });
      return response.data;
    } catch (error) {
      throw error;
    } finally {
      commit('SET_PIPELINES_UI_FLAG', { isUpdating: false });
    }
  },

  deleteStage: async ({ commit }, { accountId, pipelineId, stageId }) => {
    commit('SET_PIPELINES_UI_FLAG', { isDeleting: true });
    try {
      await PipelinesAPI.deleteStage(accountId, pipelineId, stageId);
      commit('DELETE_PIPELINE_STAGE', { pipelineId, stageId });
    } catch (error) {
      throw error;
    } finally {
      commit('SET_PIPELINES_UI_FLAG', { isDeleting: false });
    }
  },

  reorderStages: async ({ commit }, { accountId, pipelineId, stages }) => {
    commit('SET_PIPELINES_UI_FLAG', { isUpdating: true });
    try {
      const response = await PipelinesAPI.reorderStages(accountId, pipelineId, stages);
      commit('SET_PIPELINE_STAGES', { pipelineId, stages: response.data });
    } catch (error) {
      throw error;
    } finally {
      commit('SET_PIPELINES_UI_FLAG', { isUpdating: false });
    }
  },

  // ========================================
  // REALTIME ACTIONS
  // ========================================
  addPipeline({ commit }, pipeline) {
    commit('ADD_PIPELINE', pipeline);
  },

  updatePipeline({ commit }, pipeline) {
    commit('UPDATE_ITEM', pipeline);
  },

  deletePipeline({ commit }, pipelineId) {
    commit('DELETE_PIPELINE', pipelineId);
  },

  upsertRealtimeStage({ commit }, stagePayload) {
    const stage = stagePayload.data || stagePayload;
    commit('UPSERT_STAGE_IN_PIPELINE', stage);
  },

  deleteRealtimeStage({ commit }, stagePayload) {
    const stage = stagePayload.data || stagePayload;
    commit('REMOVE_STAGE_FROM_PIPELINE', stage);
  },

  reorderRealtimeStages({ commit }, payload) {
    const data = payload.data || payload;
    if (data.pipeline_id && Array.isArray(data.stages)) {
      commit('REPLACE_PIPELINE_STAGES', {
        pipelineId: data.pipeline_id,
        stages: data.stages,
      });
    }
  },
};

export const mutations = {
  SET_PIPELINES_UI_FLAG(_state, data) {
    _state.uiFlags = {
      ..._state.uiFlags,
      ...data,
    };
  },

  SET_PIPELINES(_state, data) {
    // A API retorna { data: [...] } ou array direto
    const pipelines = Array.isArray(data) ? data : data.data || [];
    _state.records = pipelines.reduce((acc, pipeline) => {
      acc[pipeline.id] = pipeline;
      return acc;
    }, {});
  },

  ADD_PIPELINE(_state, data) {
    const pipeline = data.data || data;
    _state.records = { ..._state.records, [pipeline.id]: pipeline };
    if (Array.isArray(pipeline.stages)) {
      _state.stages[pipeline.id] = [...pipeline.stages];
    }
  },

  EDIT_PIPELINE(_state, data) {
    const pipeline = data.data || data;
    _state.records = { ..._state.records, [pipeline.id]: pipeline };
    if (Array.isArray(pipeline.stages)) {
      _state.stages[pipeline.id] = [...pipeline.stages];
    }
  },

  UPDATE_ITEM(_state, item) {
    if (_state.records[item.id]) {
      _state.records[item.id] = {
        ..._state.records[item.id],
        ...item,
      };
    }
  },

  DELETE_PIPELINE(_state, id) {
    delete _state.stages[id];
    const { [id]: _, ...rest } = _state.records;
    _state.records = rest;
  },

  SET_PIPELINE_STAGES(_state, { pipelineId, stages }) {
    syncPipelineStages(_state, pipelineId, stages);
  },

  ADD_PIPELINE_STAGE(_state, { pipelineId, stage }) {
    const existingStages = _state.stages[pipelineId] || [];
    syncPipelineStages(_state, pipelineId, [...existingStages, stage]);
  },

  EDIT_PIPELINE_STAGE(_state, { pipelineId, stage }) {
    const stages = _state.stages[pipelineId] || [];
    const index = stages.findIndex(s => s.id === stage.id);
    if (index !== -1) {
      const updatedStages = [...stages];
      updatedStages[index] = stage;
      syncPipelineStages(_state, pipelineId, updatedStages);
    }
  },

  DELETE_PIPELINE_STAGE(_state, { pipelineId, stageId }) {
    const stages = _state.stages[pipelineId] || [];
    syncPipelineStages(
      _state,
      pipelineId,
      stages.filter(s => s.id !== stageId)
    );
  },

  UPSERT_STAGE_IN_PIPELINE(_state, stagePayload) {
    const stage = stagePayload.data || stagePayload;
    const pipelineId = toNumber(stage.pipeline_id);
    if (!pipelineId) return;

    const existingStages = _state.stages[pipelineId] || [];
    const index = existingStages.findIndex(s => s.id === stage.id);

    if (index === -1) {
      syncPipelineStages(_state, pipelineId, [...existingStages, stage]);
      return;
    }

    const updatedStages = [...existingStages];
    updatedStages[index] = { ...updatedStages[index], ...stage };
    syncPipelineStages(_state, pipelineId, updatedStages);
  },

  REMOVE_STAGE_FROM_PIPELINE(_state, stagePayload) {
    const stage = stagePayload.data || stagePayload;
    const stageId = toNumber(stage.id || stage.stage_id || stage);
    const pipelineId = toNumber(stage.pipeline_id);

    if (pipelineId) {
      const stages = _state.stages[pipelineId] || [];
      syncPipelineStages(
        _state,
        pipelineId,
        stages.filter(s => s.id !== stageId)
      );
      return;
    }

    Object.keys(_state.stages).forEach(id => {
      const numericId = toNumber(id);
      const stages = _state.stages[numericId] || [];
      const hasStage = stages.some(s => s.id === stageId);
      if (hasStage) {
        syncPipelineStages(
          _state,
          numericId,
          stages.filter(s => s.id !== stageId)
        );
      }
    });
  },

  REPLACE_PIPELINE_STAGES(_state, { pipelineId, stages }) {
    syncPipelineStages(_state, pipelineId, stages);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
